/**
 * Strings of the operator list (release 1, package B6) and the
 * administrator screens (iteration 2, package C6): «Об'єкти», «Заявки»,
 * «Ціни РДН». `{name}` — placeholders filled by `fill` (pages/operator).
 * Merged into `MESSAGES` (messages.ts); Ukrainian defines the key set.
 */
export const OPERATOR_MESSAGES = {
  en: {
    'operator.title': 'Clients and objects',
    'operator.sites.title': 'Objects',
    'operator.applications.title': 'Applications',
    'operator.prices.title': 'Day-ahead prices',

    'operator.count.objects.one': '{n} object',
    'operator.count.objects.few': '{n} objects',
    'operator.count.objects.many': '{n} objects',
    'operator.count.clients.one': '{n} client',
    'operator.count.clients.few': '{n} clients',
    'operator.count.clients.many': '{n} clients',
    'operator.count.applications.one': '{n} application',
    'operator.count.applications.few': '{n} applications',
    'operator.count.applications.many': '{n} applications',
    'operator.count.values.one': '{n} value',
    'operator.count.values.few': '{n} values',
    'operator.count.values.many': '{n} values',

    'operator.loading': 'Loading…',
    'operator.load_error': 'Could not load the data: {reason}.',
    'operator.reason.offline': 'the server is not responding',
    'operator.reason.session': 'the session has ended, sign in again',
    'operator.reason.forbidden': 'no administrator rights',
    'operator.reason.server': 'server error, code {status}',
    'operator.retry': 'Retry',
    'operator.forbidden.title': 'For the administrator only',
    'operator.forbidden.text': 'This section opens for the administrator account only.',
    'operator.forbidden.back': 'To objects',
    'operator.close': 'Close',
    'operator.sn': 'SN {sn}',

    'operator.sites.sub': 'all clients · {objects} · {clients}',
    'operator.sites.empty.title': 'No objects yet',
    'operator.sites.empty.text': 'Clients have not connected any inverter yet.',
    'operator.sites.online': 'Online',
    'operator.sites.offline_since': 'No connection since {time}',
    'operator.sites.vendor_down': 'DeyeCloud not responding since {time}',
    'operator.sites.history': 'History',
    'operator.sites.history.loading': 'loading: {done} of {total} days',
    'operator.sites.history.failed': 'download failed',
    'operator.sites.arbitrage': 'Internal arbitrage',
    'operator.sites.arbitrage.on': 'on',
    'operator.sites.arbitrage.off': 'off',
    'operator.sites.arbitrage.stopped': 'stopped',
    'operator.sites.application': 'VPP application',
    'operator.sites.currents': 'Battery currents',
    'operator.sites.currents.value': 'charge {charge} · discharge {discharge} · from the grid {grid}',
    'operator.sites.currents.amps': '{a} A',
    'operator.sites.currents.grid_program': '{now} (program; installer {original})',
    'operator.sites.currents.none': 'not read',
    'operator.sites.currents.volts': 'battery {v} V',
    'operator.sites.currents.read': 'read {date}',
    'operator.sites.currents.original': 'original settings {date}',
    'operator.sites.currents.grid_off': 'grid charge switched off',
    'operator.sites.currents.by_phage': 'set by Phage, not by the installer',
    'operator.sites.program_changed': 'Program changed by hand',
    'operator.sites.program_changed.value': '{at} · Phage writes nothing until a decision on the object',
    'operator.sites.limits': 'Plan within them',
    'operator.sites.limits.value': 'discharge up to {power} kW · grid charge up to {grid} kW',
    'operator.sites.limits.no_grid': 'discharge up to {power} kW · no grid charge',
    'operator.sites.limits.rated': 'inverter {rated} kW',
    'operator.sites.soc': 'charge',
    'operator.sites.soc.no_connection': 'no connection',
    'operator.sites.soc.no_data': 'no data yet',

    'operator.applications.heading': 'VPP applications',
    'operator.applications.col.date': 'Applied on',
    'operator.applications.col.client': 'Client',
    'operator.applications.col.site': 'Object',
    'operator.applications.col.potential': 'Potential over 30 days',
    'operator.applications.empty.title': 'No applications yet',
    'operator.applications.empty.text':
      'An application appears here when a client presses «Apply» in «Connection to the VPP» on the object screen.',
    'operator.applications.potential.none': 'not computed',
    'operator.applications.potential.no_osr_rate': 'no DSO rate',
    'operator.applications.potential.no_data': 'not enough data',

    'operator.prices.heading': 'Enter prices for {date}',
    'operator.prices.intro':
      "Tomorrow's day-ahead results appear on the market operator's site after lunch; Energy Map loads them later. Copy the price column and paste it here: one value per line, ₴/MWh. {date} needs {values}.",
    'operator.prices.intro.short_day': ' Clocks go forward that day.',
    'operator.prices.intro.long_day': ' Clocks go back that day.',
    'operator.prices.label': 'Prices for {date}, ₴/MWh',
    'operator.prices.placeholder': '4210.55\n3980.10\n…',
    'operator.prices.hours': 'Hours: {n}.',
    'operator.prices.save': 'Save prices for {date}',
    'operator.prices.saved': 'Prices for {date} saved.',
    'operator.prices.err.not_number': 'Line {line}: «{raw}» is not a number.',
    'operator.prices.err.ambiguous': 'Line {line}: «{raw}» — thousands or decimals? Write 5600 or 5600.00.',
    'operator.prices.err.negative': 'Line {line}: the price is below 0.',
    'operator.prices.err.count': 'Found {values}; {date} needs {hours}. Prices not saved.',
    'operator.prices.err.date_changed':
      'The day has changed: prices are now entered for {date}. Paste the prices for that day.',
    'operator.prices.err.save': 'Prices not saved: {reason}. Try again.',
  },
  uk: {
    'operator.title': "Клієнти та об'єкти",
    'operator.sites.title': "Об'єкти",
    'operator.applications.title': 'Заявки',
    'operator.prices.title': 'Ціни РДН',

    'operator.count.objects.one': "{n} об'єкт",
    'operator.count.objects.few': "{n} об'єкти",
    'operator.count.objects.many': "{n} об'єктів",
    'operator.count.clients.one': '{n} клієнт',
    'operator.count.clients.few': '{n} клієнти',
    'operator.count.clients.many': '{n} клієнтів',
    'operator.count.applications.one': '{n} заявка',
    'operator.count.applications.few': '{n} заявки',
    'operator.count.applications.many': '{n} заявок',
    'operator.count.values.one': '{n} значення',
    'operator.count.values.few': '{n} значення',
    'operator.count.values.many': '{n} значень',

    'operator.loading': 'Завантажуємо…',
    'operator.load_error': 'Не вдалося завантажити дані: {reason}.',
    'operator.reason.offline': 'сервер не відповідає',
    'operator.reason.session': 'сесія завершилась, увійдіть знову',
    'operator.reason.forbidden': 'немає прав адміністратора',
    'operator.reason.server': 'помилка сервера, код {status}',
    'operator.retry': 'Повторити',
    'operator.forbidden.title': 'Лише для адміністратора',
    'operator.forbidden.text': 'Цей розділ відкривається лише в акаунті адміністратора.',
    'operator.forbidden.back': "До об'єктів",
    'operator.close': 'Закрити',
    'operator.sn': 'SN {sn}',

    'operator.sites.sub': 'усі клієнти · {objects} · {clients}',
    'operator.sites.empty.title': "Ще немає об'єктів",
    'operator.sites.empty.text': 'Клієнти ще не підключили жодного інвертора.',
    'operator.sites.online': "На зв'язку",
    'operator.sites.offline_since': "Немає зв'язку з {time}",
    'operator.sites.vendor_down': 'DeyeCloud не відповідає з {time}',
    'operator.sites.history': 'Історія',
    'operator.sites.history.loading': 'завантажуємо: {done} з {total} днів',
    'operator.sites.history.failed': 'помилка завантаження',
    'operator.sites.arbitrage': 'Внутрішній арбітраж',
    'operator.sites.arbitrage.on': 'увімкнено',
    'operator.sites.arbitrage.off': 'вимкнено',
    'operator.sites.arbitrage.stopped': 'зупинено',
    'operator.sites.application': 'Заявка VPP',
    'operator.sites.currents': 'Струми батареї',
    'operator.sites.currents.value': 'заряд {charge} · розряд {discharge} · від мережі {grid}',
    'operator.sites.currents.amps': '{a} А',
    'operator.sites.currents.grid_program': '{now} (програма; інсталятор {original})',
    'operator.sites.currents.none': 'не прочитано',
    'operator.sites.currents.volts': 'батарея {v} В',
    'operator.sites.currents.read': 'прочитано {date}',
    'operator.sites.currents.original': 'вихідні налаштування {date}',
    'operator.sites.currents.grid_off': 'заряд від мережі вимкнено',
    'operator.sites.currents.by_phage': 'встановила Phage, а не інсталятор',
    'operator.sites.program_changed': 'Програму змінено вручну',
    'operator.sites.program_changed.value': '{at} · Phage нічого не записує до рішення на сторінці об’єкта',
    'operator.sites.limits': 'План у цих межах',
    'operator.sites.limits.value': 'розряд до {power} кВт · заряд від мережі до {grid} кВт',
    'operator.sites.limits.no_grid': 'розряд до {power} кВт · заряду від мережі немає',
    'operator.sites.limits.rated': 'інвертор {rated} кВт',
    'operator.sites.soc': 'заряд',
    'operator.sites.soc.no_connection': "немає зв'язку",
    'operator.sites.soc.no_data': 'ще немає даних',

    'operator.applications.heading': 'Заявки на VPP',
    'operator.applications.col.date': 'Дата заявки',
    'operator.applications.col.client': 'Клієнт',
    'operator.applications.col.site': "Об'єкт",
    'operator.applications.col.potential': 'Потенціал за 30 днів',
    'operator.applications.empty.title': 'Заявок ще немає',
    'operator.applications.empty.text':
      "Заявка з'являється тут, коли клієнт натискає «Подати заявку» в «Підключення до VPP» на екрані об'єкта.",
    'operator.applications.potential.none': 'не розраховано',
    'operator.applications.potential.no_osr_rate': 'немає ставки ОСР',
    'operator.applications.potential.no_data': 'мало даних',

    'operator.prices.heading': 'Внести ціни на {date}',
    'operator.prices.intro':
      'Результати РДН на завтра видно на сайті оператора ринку після обіду, Energy Map завантажує їх пізніше. Скопіюйте стовпець цін і вставте сюди: одне значення на рядок, ₴/МВт·год. Для {date} потрібно {values}.',
    'operator.prices.intro.short_day': ' Це доба переходу на літній час.',
    'operator.prices.intro.long_day': ' Це доба переходу на зимовий час.',
    'operator.prices.label': 'Ціни на {date}, ₴/МВт·год',
    'operator.prices.placeholder': '4 210,55\n3 980,10\n…',
    'operator.prices.hours': 'Годин: {n}.',
    'operator.prices.save': 'Зберегти ціни на {date}',
    'operator.prices.saved': 'Ціни на {date} збережено.',
    'operator.prices.err.not_number': 'Рядок {line}: «{raw}» — не число.',
    'operator.prices.err.ambiguous':
      'Рядок {line}: «{raw}» — тисячі чи дробова частина? Запишіть 5600 або 5600,00.',
    'operator.prices.err.negative': 'Рядок {line}: ціна менша за 0.',
    'operator.prices.err.count': 'Знайдено {values}, для {date} потрібно {hours}. Ціни не збережено.',
    'operator.prices.err.date_changed':
      'Доба змінилася: тепер ціни вносяться на {date}. Вставте ціни на цю добу.',
    'operator.prices.err.save': 'Ціни не збережено: {reason}. Спробуйте ще раз.',
  },
} as const;
